/* SCLARE theme | theme.js | no dependencies */
(() => {
  'use strict';

  const T = window.theme || { routes: {}, strings: {} };
  const S = T.strings || {};
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
  const mq = (q) => window.matchMedia(q);
  const reduceMotion = mq('(prefers-reduced-motion: reduce)');
  const desktop = mq('(min-width: 990px)');
  const clamp = (x) => Math.max(0, Math.min(1, x));
  const seg = (p, a, b) => clamp((p - a) / (b - a));
  const eOut = (t) => 1 - Math.pow(1 - t, 3);
  const eInOut = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
  const eBack = (t) => { const c1 = 1.25, c3 = c1 + 1; return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2); };

  /* ---------- helpers ---------- */
  const toastEl = $('#Toast');
  function toast(msg) {
    if (!toastEl || !msg) return;
    toastEl.textContent = msg;
    toastEl.classList.add('is-visible');
    clearTimeout(toast.t);
    toast.t = setTimeout(() => toastEl.classList.remove('is-visible'), 2600);
  }

  let locks = 0;
  const isLocked = () => locks > 0;
  function lockScroll(on) {
    const root = document.documentElement;
    const wasLocked = locks > 0;
    locks = Math.max(0, locks + (on ? 1 : -1));
    const nowLocked = locks > 0;
    if (nowLocked === wasLocked) return;
    if (nowLocked) {
      // hiding the page scrollbar makes the viewport wider; reserve the same
      // width so the header and the page never jump sideways
      const gutter = window.innerWidth - root.clientWidth;
      root.style.setProperty('--scrollbar-gutter', `${gutter > 0 ? gutter : 0}px`);
    }
    root.classList.toggle('is-locked', nowLocked);
    if (!nowLocked) root.style.removeProperty('--scrollbar-gutter');
  }

  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  function trapFocus(container, e) {
    if (e.key !== 'Tab') return;
    const items = $$(FOCUSABLE, container).filter((el) => el.offsetParent !== null);
    if (!items.length) return;
    const first = items[0], last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  function formatMoney(cents, format) {
    format = format || T.moneyFormat || '{{amount}}';
    const re = /\{\{\s*(\w+)\s*\}\}/;
    const fmt = (n, prec, thou, dec) => {
      const fixed = (n / 100).toFixed(prec);
      const parts = fixed.split('.');
      return parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thou) + (parts[1] ? dec + parts[1] : '');
    };
    const m = format.match(re);
    if (!m) return format;
    let v;
    switch (m[1]) {
      case 'amount_no_decimals': v = fmt(cents, 0, ',', '.'); break;
      case 'amount_with_comma_separator': v = fmt(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': v = fmt(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': v = fmt(cents, 2, "'", '.'); break;
      case 'amount_no_decimals_with_space_separator': v = fmt(cents, 0, ' ', ''); break;
      case 'amount_with_space_separator': v = fmt(cents, 2, ' ', ','); break;
      case 'amount_with_period_and_space_separator': v = fmt(cents, 2, ' ', '.'); break;
      default: v = fmt(cents, 2, ',', '.');
    }
    const tmp = document.createElement('div');
    tmp.innerHTML = format.replace(re, v);
    return tmp.textContent;
  }

  /* ---------- header ---------- */
  function initHeader() {
    const header = $('[data-header]');
    if (!header) return;

    // while the page is locked the scroll position cannot really change, so any
    // scroll event is a side effect of the lock and must not restyle the header
    const onScroll = () => {
      if (isLocked()) return;
      header.classList.toggle('is-solid', window.scrollY > 8);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });

    const group = header.closest('.shopify-section-group-header-group') || header;
    const measure = () => {
      const h = Math.round(group.getBoundingClientRect().height);
      if (h) document.documentElement.style.setProperty('--sticky-h', `${h}px`);
      return h;
    };
    let groupH = measure();
    window.addEventListener('resize', () => {
      if (isLocked()) return;
      groupH = measure();
      build();
    }, { passive: true });

    // the header goes light only when a dark section sits directly beneath it
    const darks = $$('[data-dark]');
    let io = null;
    function build() {
      if (!('IntersectionObserver' in window) || !darks.length) return;
      if (io) io.disconnect();
      const bottom = Math.max(0, window.innerHeight - groupH - 2);
      const active = new Set();
      io = new IntersectionObserver((entries) => {
        entries.forEach((en) => (en.isIntersecting ? active.add(en.target) : active.delete(en.target)));
        if (isLocked()) return;
        header.classList.toggle('is-dark', active.size > 0);
      }, { rootMargin: `0px 0px -${bottom}px 0px` });
      darks.forEach((d) => io.observe(d));
    }
    build();

    const menu = $('[data-menu]');
    const openBtn = $('[data-menu-open]');
    if (!menu || !openBtn) return;
    let closeTimer;
    const onKey = (e) => { if (e.key === 'Escape') close(); else trapFocus(menu, e); };
    function open() {
      clearTimeout(closeTimer);
      menu.hidden = false;
      requestAnimationFrame(() => menu.classList.add('is-open'));
      openBtn.setAttribute('aria-expanded', 'true');
      lockScroll(true);
      document.addEventListener('keydown', onKey);
      const first = $('[data-menu-close]', menu);
      if (first) first.focus();
    }
    function close(returnFocus = true) {
      if (menu.hidden) return;
      menu.classList.remove('is-open');
      openBtn.setAttribute('aria-expanded', 'false');
      lockScroll(false);
      document.removeEventListener('keydown', onKey);
      closeTimer = setTimeout(() => { menu.hidden = true; }, reduceMotion.matches ? 0 : 550);
      if (returnFocus) openBtn.focus();
    }
    openBtn.addEventListener('click', open);
    $$('[data-menu-close]', menu).forEach((b) => b.addEventListener('click', () => close()));
    menu.addEventListener('click', (e) => { if (e.target.closest('a')) close(false); });

  }

  /* ---------- cart ---------- */
  const Cart = {
    drawer: null,
    opener: null,

    init() {
      this.drawer = $('[data-cart-drawer]');
      document.addEventListener('click', (e) => {
        const openEl = e.target.closest('[data-cart-open]');
        if (openEl && this.drawer && T.cartType === 'drawer') {
          e.preventDefault();
          this.open(openEl);
          return;
        }
        if (!this.drawer || !this.drawer.contains(e.target)) return;
        if (e.target.closest('[data-cart-close]')) { this.close(); return; }
        const q = e.target.closest('[data-qty-change]');
        if (q) {
          const line = q.closest('[data-line]');
          if (line) this.change(Number(line.dataset.line), Number(q.dataset.qtyChange));
        }
      });
      if (this.drawer) {
        this.drawer.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') this.close();
          else trapFocus($('.cart-drawer__panel', this.drawer), e);
        });
      }
    },

    open(opener) {
      if (!this.drawer) { window.location.href = T.routes.cart; return; }
      this.opener = opener || document.activeElement;
      this.drawer.removeAttribute('inert');
      this.drawer.setAttribute('aria-hidden', 'false');
      this.drawer.classList.add('is-open');
      lockScroll(true);
      const panel = $('.cart-drawer__panel', this.drawer);
      setTimeout(() => panel && panel.focus(), 60);
    },

    close() {
      if (!this.drawer || !this.drawer.classList.contains('is-open')) return;
      this.drawer.classList.remove('is-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      this.drawer.setAttribute('inert', '');
      lockScroll(false);
      if (this.opener && this.opener.focus) this.opener.focus();
    },

    async add(body, opener) {
      body.append('sections', 'cart-drawer');
      body.append('sections_url', window.location.pathname);
      const res = await fetch(`${T.routes.cartAdd}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.status) throw new Error(data.description || data.message || S.error);
      await this.render(data.sections);
      toast(S.added);
      if (T.cartType === 'drawer' && this.drawer) this.open(opener);
      return data;
    },

    async change(line, quantity) {
      const inner = $('[data-cart-inner]', this.drawer);
      if (inner) { inner.classList.add('is-busy'); inner.setAttribute('aria-busy', 'true'); }
      try {
        const res = await fetch(`${T.routes.cartChange}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line, quantity, sections: 'cart-drawer', sections_url: window.location.pathname })
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || data.status) throw new Error(data.description || data.message || S.error);
        this.render(data.sections);
        const panel = $('.cart-drawer__panel', this.drawer);
        if (panel) panel.focus();
      } catch (err) {
        const cur = $('[data-cart-inner]', this.drawer);
        if (cur) { cur.classList.remove('is-busy'); cur.removeAttribute('aria-busy'); }
        const errEl = $('[data-cart-error]', this.drawer);
        if (errEl) { errEl.textContent = err.message || S.error; errEl.hidden = false; }
        else toast(err.message || S.error);
      }
    },

    render(sections) {
      const html = sections && sections['cart-drawer'];
      if (!html) return this.refreshCount();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('[data-cart-inner]');
      if (!fresh) return this.refreshCount();
      const cur = this.drawer && $('[data-cart-inner]', this.drawer);
      if (cur) cur.replaceWith(fresh);
      this.setCount(fresh.dataset.count);
      return Promise.resolve();
    },

    setCount(n) { $$('[data-cart-count]').forEach((el) => { el.textContent = n; }); },

    async refreshCount() {
      try {
        const res = await fetch(`${T.routes.cart}.js`, { headers: { Accept: 'application/json' } });
        const c = await res.json();
        this.setCount(c.item_count);
      } catch (e) { /* count stays as rendered */ }
    }
  };

  /* ---------- quick add on product cards ---------- */
  function initQuickAdd(card) {
    if (card.dataset.quickReady) return;
    card.dataset.quickReady = '1';
    const panel = $('[data-card-panel]', card);
    const openBtn = $('[data-card-open]', card);

    // simple one-variant card: the image is the button
    if (!panel) {
      card.addEventListener('click', async (e) => {
        const btn = e.target.closest('[data-card-add]');
        if (!btn || btn.disabled) return;
        e.preventDefault();
        await addFromCard(btn, btn.dataset.cardAdd, 1);
      });
      return;
    }

    const json = $('[data-variants]', card);
    const variants = json ? JSON.parse(json.textContent) : [];
    const groups = $$('[data-card-option]', panel);
    const colorGroup = groups.find((g) => isColorName(g.dataset.optionName));
    const colorIndex = colorGroup ? Number(colorGroup.dataset.cardOption) : -1;
    const mainImg = $('.card__img', card);
    const originalSrc = mainImg ? { src: mainImg.getAttribute('src'), srcset: mainImg.getAttribute('srcset') } : null;
    let lastColor;
    const submit = $('[data-card-submit]', panel);
    const label = $('[data-card-label]', panel);
    const errorEl = $('[data-card-error]', panel);
    let variant = null;

    const selection = () => {
      const sel = [];
      groups.forEach((g) => {
        const i = Number(g.dataset.cardOption);
        const chosen = $('.card__chip.is-selected', g);
        sel[i] = chosen ? chosen.dataset.value : null;
      });
      // options with a single value are fixed, so read them off any variant
      if (variants.length && groups.length) {
        variants[0].options.forEach((v, i) => {
          if (!groups.some((g) => Number(g.dataset.cardOption) === i)) sel[i] = v;
        });
      }
      return sel;
    };

    function update() {
      const sel = selection();
      groups.forEach((g) => {
        const i = Number(g.dataset.cardOption);
        const chosen = $('.card__chip.is-selected', g);
        const out = $('[data-card-chosen]', g);
        if (out) out.textContent = chosen ? `: ${chosen.dataset.value}` : '';
        $$('.card__chip', g).forEach((chip) => {
          const ok = variants.some((v) => v.available && v.options[i] === chip.dataset.value &&
            sel.every((s, j) => j === i || s == null || v.options[j] === s));
          chip.classList.toggle('is-soldout', !ok);
          chip.disabled = !ok;
          if (!ok) chip.classList.remove('is-selected');
        });
      });
      // never guess: without a picker for every option we refuse to resolve a variant
      // show the chosen colour on the card itself
      if (mainImg && colorIndex >= 0 && sel[colorIndex] !== lastColor) {
        lastColor = sel[colorIndex];
        const match = lastColor ? variants.find((v) => v.options[colorIndex] === lastColor && v.featured_image) : null;
        if (match) {
          mainImg.removeAttribute('srcset');
          mainImg.src = match.featured_image.src;
        } else if (originalSrc) {
          mainImg.setAttribute('src', originalSrc.src);
          if (originalSrc.srcset) mainImg.setAttribute('srcset', originalSrc.srcset);
        }
      }

      const complete = groups.length > 0 && sel.every((s) => s != null);
      variant = complete ? variants.find((v) => v.options.every((o, i) => o === sel[i])) || null : null;
      if (label) label.textContent = variant && !variant.available ? S.soldOut : S.addToCart;
      if (submit) submit.disabled = !!(variant && !variant.available);
    }

    function missing() {
      const sel = selection();
      const g = groups.find((x) => sel[Number(x.dataset.cardOption)] == null);
      if (!g) return S.unavailable;
      const name = g.dataset.optionName || '';
      return isSizeName(name) ? S.selectSize : (S.selectOption || '').replace('[option]', name.toLowerCase());
    }

    const showError = (m) => { if (errorEl) { errorEl.textContent = m; errorEl.hidden = false; } else toast(m); };
    const hideError = () => { if (errorEl) { errorEl.hidden = true; errorEl.textContent = ''; } };

    const scrim = $('[data-card-scrim]', card) || $('.card__scrim', card);
    let isOpen = false, hideTimer;
    const onKey = (e) => {
      if (e.key === 'Escape') { e.stopPropagation(); close(true); }
      else trapFocus(panel, e);
    };
    const close = (focusBack) => {
      if (!isOpen) return;
      isOpen = false;
      card.classList.remove('is-open');
      hideError();
      lockScroll(false);
      document.removeEventListener('keydown', onKey);
      clearTimeout(hideTimer);
      hideTimer = setTimeout(() => {
        panel.hidden = true;
        if (scrim) scrim.hidden = true;
      }, reduceMotion.matches ? 0 : 400);
      if (openBtn) openBtn.setAttribute('aria-expanded', 'false');
      if (focusBack && openBtn) openBtn.focus();
    };
    const open = () => {
      if (isOpen) return;
      $$('[data-quick-add].is-open').forEach((o) => { if (o !== card) o.dispatchEvent(new CustomEvent('quick:close')); });
      isOpen = true;
      clearTimeout(hideTimer);
      panel.hidden = false;
      if (scrim) scrim.hidden = false;
      requestAnimationFrame(() => card.classList.add('is-open'));
      lockScroll(true);
      document.addEventListener('keydown', onKey);
      if (openBtn) openBtn.setAttribute('aria-expanded', 'true');
      const first = $('.card__chip:not(:disabled)', panel);
      if (first) first.focus();
    };
    card.addEventListener('quick:close', () => close(false));

    card.addEventListener('click', async (e) => {
      if (e.target.closest('[data-card-open]')) { e.preventDefault(); open(); return; }
      if (e.target.closest('[data-card-close]')) { e.preventDefault(); close(true); return; }
      const chip = e.target.closest('.card__chip');
      if (chip && !chip.disabled) {
        e.preventDefault();
        const group = chip.closest('[data-card-option]');
        $$('.card__chip', group).forEach((c) => c.classList.remove('is-selected'));
        chip.classList.add('is-selected');
        hideError();
        update();
        return;
      }
      if (e.target.closest('[data-card-submit]')) {
        e.preventDefault();
        if (!variant) { showError(missing()); return; }
        if (!variant.available) { showError(S.soldOut); return; }
        const ok = await addFromCard(submit, variant.id, 1);
        if (ok) close(false);
      }
    });

    update();
  }

  async function addFromCard(btn, id, quantity) {
    btn.disabled = true;
    btn.setAttribute('aria-busy', 'true');
    const body = new FormData();
    body.append('id', id);
    body.append('quantity', String(quantity));
    try {
      await Cart.add(body, btn);
      return true;
    } catch (err) {
      toast(err.message || S.error);
      return false;
    } finally {
      btn.disabled = false;
      btn.removeAttribute('aria-busy');
    }
  }

  /* ---------- product gallery ---------- */
  const handleize = (v) => String(v == null ? '' : v).toLowerCase().trim()
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

  function initGallery(g) {
    const track = $('[data-media-track]', g);
    const index = $('[data-media-index]', g);
    const total = $('[data-media-total]', g);
    if (!track) return;
    const items = $$('.pm__item', track);
    // normalise once: works whether the markup carries "Light Pink" or "light-pink-2"
    items.forEach((el) => { if (el.dataset.mediaGroup) el._group = handleize(el.dataset.mediaGroup); });
    const prev = $('[data-media-prev]', g);
    const next = $('[data-media-next]', g);
    const count = $('.pm__count', g);
    let raf = 0;

    const visible = () => items.filter((el) => !el.hidden)
      .sort((a, b) => a.offsetLeft - b.offsetLeft);
    const current = () => (track.clientWidth ? Math.round(track.scrollLeft / track.clientWidth) : 0);

    function sync() {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const shown = visible();
        const i = Math.min(Math.max(current(), 0), Math.max(shown.length - 1, 0));
        if (index) index.textContent = String(i + 1);
        if (prev) prev.disabled = i <= 0;
        if (next) next.disabled = i >= shown.length - 1;
      });
    }
    track.addEventListener('scroll', sync, { passive: true });

    const step = (dir) => {
      const shown = visible();
      const i = Math.min(Math.max(current() + dir, 0), Math.max(shown.length - 1, 0));
      const target = shown[i];
      if (target) track.scrollTo({ left: target.offsetLeft, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    };
    if (prev) prev.addEventListener('click', () => step(-1));
    if (next) next.addEventListener('click', () => step(1));
    track.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') { e.preventDefault(); step(1); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); step(-1); }
    });

    g.goTo = (mediaId) => {
      const item = $(`[data-media-id="${mediaId}"]`, track);
      if (!item || item.hidden) return;
      track.scrollTo({ left: item.offsetLeft, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    };

    // show only the photos that belong to the chosen colour, ordered by the
    // number in their alt text so -1 is always slide 1
    g.setGroup = (value, featuredId) => {
      if (!g.hasAttribute('data-media-grouped')) return;
      const tagged = items.filter((el) => el._group);
      if (!tagged.length) return;
      const wanted = value == null ? null : handleize(value);
      const matches = wanted === null ? [] : tagged.filter((el) => el._group === wanted);

      items.forEach((el) => {
        const group = el._group;
        let show;
        if (wanted === null) {
          show = true;                              // no colour picked yet
        } else if (matches.length) {
          // strictly this colour only. An image with no colour in its alt text is
          // far more likely to be unlabelled than deliberately shared, and showing
          // it would mix colours back in.
          show = group === wanted;
        } else {
          // nothing carries this colour's alt text: fall back to the variant's
          // own image rather than showing every colour at once
          show = featuredId != null
            ? String(el.dataset.mediaId) === String(featuredId)
            : !group;
        }
        el.hidden = !show;
        const seq = Number(el.dataset.mediaSeq);
        el.style.order = seq > 0 ? String(seq) : '';
        if (show) {
          const img = el.querySelector('img[loading="lazy"]');
          if (img) img.loading = 'eager';
        }
      });

      const shown = visible();
      if (total) total.textContent = String(shown.length);
      // a single image needs no navigation
      const solo = shown.length < 2;
      [prev, next].forEach((btn) => { if (btn) btn.hidden = solo; });
      if (count) count.hidden = solo;
      // back to the first slide of the new colour
      requestAnimationFrame(() => {
        track.scrollTo({ left: 0, top: 0, behavior: 'auto' });
        sync();
      });
    };

    sync();
  }

  /* ---------- product form ---------- */
  const isSizeName = (name) => /size|storlek|gr(ö|o)(ß|ss)e|taille|talla|taglia|maat/i.test(name || '');
  const isColorName = (name) => /colou?r|färg|farg|farbe|couleur|kleur/i.test(name || '');

  class ProductForm {
    constructor(wrap) {
      this.wrap = wrap;
      this.form = $('form[data-product-form]', wrap);
      if (!this.form) return;
      this.section = wrap.closest('[data-product-section]') || document;
      const json = $('[data-variants]', wrap);
      this.variants = json ? JSON.parse(json.textContent) : [];
      this.idInput = $('[data-variant-id]', this.form);
      this.idInput.disabled = false;
      this.fieldsets = $$('.pf__opt', this.form);
      this.addBtn = $('[data-add-button]', this.form);
      this.addLabel = $('[data-add-label]', this.form);
      this.errorEl = $('[data-form-error]', this.form);
      this.dynamic = $('[data-dynamic-checkout]', this.form);
      this.sticky = $('[data-sticky-add]', this.section);
      this.stickyPrice = $('[data-price-sticky]', this.section);
      this.gallery = $('[data-media-gallery]', this.section);
      this.colorFieldset = this.fieldsets.find((f) => isColorName(f.dataset.optionName));
      this.colorIndex = this.colorFieldset ? Number(this.colorFieldset.dataset.optionIndex) : -1;
      this.busy = false;
      this.anyAvailable = this.variants.some((v) => v.available);

      this.form.addEventListener('change', (e) => { if (e.target.matches('.pf__radio')) this.update(true); });
      this.form.addEventListener('submit', (e) => this.submit(e));
      $$('[data-qty-step]', this.form).forEach((b) => b.addEventListener('click', () => {
        const input = $('input[name="quantity"]', this.form);
        input.value = Math.max(1, (parseInt(input.value, 10) || 1) + Number(b.dataset.qtyStep));
      }));
      const guard = $('[data-dynamic-guard]', this.form);
      if (guard) guard.addEventListener('click', () => this.explainMissing());

      this.update(false);
    }

    selection() {
      return this.fieldsets.map((fs) => {
        const c = $('.pf__radio:checked', fs);
        return c ? c.value : null;
      });
    }

    update(fromUser) {
      if (!this.fieldsets.length) {
        this.variant = this.variants.find((v) => String(v.id) === this.idInput.value) || this.variants[0] || null;
        this.render(true, fromUser);
        return;
      }
      const sel = this.selection();
      this.fieldsets.forEach((fs, i) => {
        const chosen = $('[data-chosen]', fs);
        if (chosen) chosen.textContent = sel[i] ? `: ${sel[i]}` : '';
        $$('.pf__radio', fs).forEach((radio) => {
          const ok = this.variants.some((v) => v.available && v.options[i] === radio.value &&
            sel.every((s, j) => j === i || s === null || v.options[j] === s));
          const label = $(`label[for="${radio.id}"]`, fs);
          if (!label) return;
          label.classList.toggle('is-soldout', !ok);
          const hint = $('[data-soldout]', label);
          if (hint) hint.textContent = ok ? '' : `, ${S.soldOut}`;
        });
      });
      const complete = sel.every((s) => s !== null);
      this.variant = complete ? (this.variants.find((v) => v.options.every((o, i) => o === sel[i])) || null) : null;
      this.render(complete, fromUser);
    }

    setButton(label, disabled) {
      if (this.addLabel) this.addLabel.textContent = label;
      if (this.addBtn) this.addBtn.disabled = disabled;
      if (this.sticky) { this.sticky.textContent = label; this.sticky.disabled = disabled; }
    }

    setDynamic(enabled) {
      this.form.classList.toggle('is-incomplete', !enabled);
      if (this.dynamic) this.dynamic.toggleAttribute('inert', !enabled);
    }

    syncGallery() {
      if (!this.gallery || !this.gallery.setGroup || this.colorIndex < 0) return;
      const chosen = this.selection()[this.colorIndex];
      if (chosen === this.lastColor) return;
      this.lastColor = chosen;
      // Shopify allows one featured image per variant, so that one leads and the
      // rest of the colour's photos follow behind it
      const lead = chosen
        ? this.variants.find((v) => v.options[this.colorIndex] === chosen && v.featured_media)
        : null;
      this.gallery.setGroup(chosen, lead ? lead.featured_media.id : null);
    }

    render(complete, fromUser) {
      const v = this.variant;
      this.syncGallery();
      if (!complete) {
        this.idInput.value = '';
        this.setButton(this.anyAvailable ? S.addToCart : S.soldOut, !this.anyAvailable);
        this.setDynamic(false);
        return;
      }
      if (!v) {
        this.idInput.value = '';
        this.setButton(S.unavailable, true);
        this.setDynamic(false);
        return;
      }
      this.idInput.value = v.id;
      this.setButton(v.available ? S.addToCart : S.soldOut, !v.available);
      this.setDynamic(v.available);
      if (fromUser) this.hideError();
      this.renderPrice(v);
      if (fromUser) {
        if (window.history.replaceState && this.section !== document) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', v.id);
          window.history.replaceState({}, '', url.toString());
        }
        if (this.colorIndex < 0 && v.featured_media && this.gallery && this.gallery.goTo) this.gallery.goTo(v.featured_media.id);
      }
    }

    renderPrice(v) {
      $$('[data-price]', this.section).forEach((el) => {
        const sale = v.compare_at_price && v.compare_at_price > v.price;
        el.classList.toggle('price--sale', !!sale);
        el.textContent = '';
        const now = document.createElement('span');
        now.className = 'price__now';
        now.textContent = formatMoney(v.price);
        el.append(now);
        if (sale) {
          const was = document.createElement('s');
          was.className = 'price__was';
          was.textContent = formatMoney(v.compare_at_price);
          el.append(' ', was);
        }
      });
      if (this.stickyPrice) this.stickyPrice.textContent = formatMoney(v.price);
    }

    showError(msg) {
      if (!this.errorEl) { toast(msg); return; }
      this.errorEl.textContent = msg;
      this.errorEl.hidden = false;
    }
    hideError() { if (this.errorEl) { this.errorEl.hidden = true; this.errorEl.textContent = ''; } }

    explainMissing() {
      const sel = this.selection();
      const i = sel.findIndex((s) => s === null);
      if (i === -1) { this.showError(S.unavailable); return; }
      const fs = this.fieldsets[i];
      const name = fs.dataset.optionName || '';
      this.showError(isSizeName(name) ? S.selectSize : (S.selectOption || '').replace('[option]', name.toLowerCase()));
      fs.scrollIntoView({ block: 'center', behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      const first = $('.pf__radio', fs);
      if (first) first.focus({ preventScroll: true });
    }

    async submit(e) {
      e.preventDefault();
      if (this.busy) return;
      if (!this.variant) { this.explainMissing(); return; }
      if (!this.variant.available) return;

      this.busy = true;
      clearTimeout(this.resetTimer);
      this.hideError();
      if (this.addBtn) { this.addBtn.disabled = true; this.addBtn.setAttribute('aria-busy', 'true'); }
      if (this.addLabel) this.addLabel.textContent = S.adding;
      if (this.sticky) { this.sticky.disabled = true; this.sticky.textContent = S.adding; }

      let ok = false;
      try {
        await Cart.add(new FormData(this.form), this.addBtn);
        ok = true;
      } catch (err) {
        this.showError(err.message || S.error);
      } finally {
        this.busy = false;
        if (this.addBtn) this.addBtn.removeAttribute('aria-busy');
        const label = ok ? S.added : S.addToCart;
        this.setButton(label, false);
        if (ok) this.resetTimer = setTimeout(() => this.render(true, false), 1600);
      }
    }
  }

  function initStickyAtc(section) {
    const bar = $('[data-sticky-atc]', section);
    const buy = $('[data-buy-buttons]', section);
    if (!bar || !buy || !('IntersectionObserver' in window)) return;
    const set = (show) => {
      bar.classList.toggle('is-visible', show);
      bar.toggleAttribute('inert', !show);
      bar.setAttribute('aria-hidden', String(!show));
      document.body.classList.toggle('has-sticky-atc', show);
    };
    let visible = true;
    new IntersectionObserver(([en]) => {
      visible = en.isIntersecting;
      set(!visible && !desktop.matches);
    }).observe(buy);
    if (desktop.addEventListener) desktop.addEventListener('change', () => set(!visible && !desktop.matches));
  }

  /* ---------- size guide dialog ---------- */
  function initDialogs() {
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-size-guide]');
      if (trigger) {
        const d = document.getElementById(trigger.dataset.sizeGuide);
        if (d && typeof d.showModal === 'function') { e.preventDefault(); d.showModal(); }
        return;
      }
      const closeBtn = e.target.closest('[data-modal-close]');
      if (closeBtn) { const d = closeBtn.closest('dialog'); if (d) d.close(); return; }
      if (e.target.tagName === 'DIALOG') e.target.close();
    });
  }

  /* ---------- hero ---------- */
  function initHero() {
    const hero = $('[data-hero]');
    if (!hero) return;
    requestAnimationFrame(() => requestAnimationFrame(() => hero.classList.add('is-in')));
  }

  /* ---------- logo animation ---------- */
  function initLeap(sec) {
    if (reduceMotion.matches || !('IntersectionObserver' in window)) return;
    const ring = $('[data-leap-ring]', sec);
    const cat = $('[data-leap-cat]', sec);
    if (!ring || !cat) return;
    const sheen = $('[data-leap-sheen]', sec);
    const tag = $('[data-leap-tag]', sec);
    const cta = $('[data-leap-cta]', sec);
    const echoes = $$('[data-leap-echo]', sec);
    const box = $('[data-leap-lines]', sec);
    const big = desktop.matches;

    ring.loading = 'eager';
    cat.loading = 'eager';
    sec.classList.add('leap--animate');

    const lines = [];
    const count = big ? 30 : 12;
    for (let i = 0; i < count; i++) {
      const el = document.createElement('i');
      el.style.top = `${(30 + Math.random() * 40).toFixed(2)}%`;
      el.style.width = `${(16 + Math.random() * 40).toFixed(1)}vw`;
      el.style.height = `${Math.random() < 0.25 ? 2 : 1}px`;
      box.appendChild(el);
      lines.push({ el, s: 0.16 + Math.random() * 0.3, d: 0.12 + Math.random() * 0.14 });
    }

    const stage = $('.leap__stage', sec);
    // land the artwork on whole device pixels so the browser never resamples it
    function snap() {
      if (!stage) return;
      stage.style.setProperty('--snap-x', '0px');
      stage.style.setProperty('--snap-y', '0px');
      const dpr = window.devicePixelRatio || 1;
      const r = stage.getBoundingClientRect();
      const off = (v) => {
        const d = Math.round(v * dpr) / dpr - v;
        return Math.abs(d) < 0.001 ? 0 : d;
      };
      stage.style.setProperty('--snap-x', `${off(r.left).toFixed(3)}px`);
      stage.style.setProperty('--snap-y', `${off(r.top).toFixed(3)}px`);
    }

    let active = false, raf = 0, lastP = -1;
    const show = (el, o) => { el.style.opacity = o.toFixed(3); el.style.visibility = o > 0.001 ? 'visible' : 'hidden'; };

    function frame() {
      raf = 0;
      const r = sec.getBoundingClientRect();
      const total = Math.max(1, sec.offsetHeight - window.innerHeight);
      const p = clamp(-r.top / total);
      if (p !== lastP) {
        lastP = p;
        const rp = eOut(seg(p, 0, 0.3));
        ring.style.transform = `rotateX(${(1 - rp) * 86}deg) rotateZ(${(1 - rp) * -6}deg) scale(${0.62 + 0.38 * rp})`;
        ring.style.opacity = (0.55 + 0.45 * rp).toFixed(3);

        // once the jaguar has landed, show the untouched logo artwork instead of the motion layers
        const settled = p >= 0.5;
        if (settled !== sec.classList.contains('is-settled')) {
          sec.classList.toggle('is-settled', settled);
          if (settled) snap();
        }

        const cp = seg(p, 0.27, 0.5), cb = eBack(cp), ce = eOut(cp);
        cat.style.transform = `translate3d(${(1 - cb) * -95}vw,0,0) skewX(${(1 - ce) * -22}deg) scaleX(${1 + (1 - ce) * 0.5})`;
        cat.style.opacity = cp > 0 ? Math.min(1, cp * 5).toFixed(3) : '0';
        if (big) cat.style.filter = ce < 1 ? `blur(${((1 - ce) * 14).toFixed(2)}px)` : 'none';

        const sp = seg(p, 0.5, 0.64);
        if (sheen) {
          sheen.style.setProperty('--x', `${(-30 + sp * 160).toFixed(1)}%`);
          show(sheen, sp > 0 && sp < 1 ? Math.sin(sp * Math.PI) : 0);
        }

        echoes.forEach((e, i) => {
          const t = seg(p, 0.5 + i * 0.05, 0.74 + i * 0.05);
          e.style.transform = `scale(${1 + eOut(t) * (0.35 + i * 0.12)})`;
          show(e, t > 0 && t < 1 ? (1 - t) * 0.7 : 0);
        });

        lines.forEach((l) => {
          const t = seg(p, l.s, l.s + l.d);
          l.el.style.transform = `translate3d(${-60 + t * 220}vw,0,0)`;
          show(l.el, t > 0 && t < 1 ? Math.sin(t * Math.PI) * 0.9 : 0);
        });

        if (tag) {
          const tp = eInOut(seg(p, 0.62, 0.8));
          tag.style.clipPath = `inset(-20% ${((1 - tp) * 100).toFixed(2)}% -20% 0)`;
        }
        if (cta) {
          const ct = eOut(seg(p, 0.82, 0.92));
          cta.style.opacity = ct.toFixed(3);
          cta.style.transform = `translateY(${((1 - ct) * 14).toFixed(1)}px)`;
          cta.style.visibility = ct > 0.02 ? 'visible' : 'hidden';
        }
      }
    }
    function schedule() { if (active && !raf) raf = requestAnimationFrame(frame); }

    new IntersectionObserver(([en]) => {
      active = en.isIntersecting;
      if (active) { lastP = -1; schedule(); }
    }, { rootMargin: '200px 0px' }).observe(sec);
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', () => { lastP = -1; if (sec.classList.contains('is-settled')) snap(); schedule(); }, { passive: true });
    frame();
  }

  /* ---------- boot ---------- */
  function boot() {
    initHeader();
    Cart.init();
    initDialogs();
    initHero();
    $$('[data-quick-add]').forEach(initQuickAdd);
    $$('[data-media-gallery]').forEach(initGallery);
    $$('[data-product-form-wrap]').forEach((w) => new ProductForm(w));
    $$('[data-product-section]').forEach(initStickyAtc);
    $$('[data-leap]').forEach(initLeap);

    document.addEventListener('shopify:section:load', (e) => {
      const s = e.target;
      $$('[data-quick-add]', s).forEach(initQuickAdd);
      $$('[data-media-gallery]', s).forEach(initGallery);
      $$('[data-product-form-wrap]', s).forEach((w) => new ProductForm(w));
      $$('[data-product-section]', s).forEach(initStickyAtc);
      $$('[data-leap]', s).forEach(initLeap);
      if ($('[data-hero]', s)) initHero();
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
